import type { QueryClient, QueryKey } from '@tanstack/react-query'
import { applyEnvelope, type Envelope, type HasSections } from './envelope'

/**
 * Writing one mutation envelope into the several cache entries a domain root is
 * spread across.
 *
 * Three of the four domains split one root over two reads, because the API does:
 * a campaign is its sheet plus its world graph, a scene is its sheet plus its
 * snapshot, an encounter is its sheet plus its combat cards. The heavy half is a
 * separate endpoint precisely so most opens never pay for it — which makes it a
 * separate cache entry, and makes a single write potentially relevant to both.
 */

/**
 * Restricts an envelope to the sections a given cache entry already holds.
 *
 * This is the whole reason this module exists. A write can touch sections that live
 * on different sides of the split: adding a faction returns `factions` *and* `city`,
 * because the city's faction count moved; adding a scene character returns
 * `characters` *and* `snapshot`'s staleness count. Each entry should take the parts
 * it is responsible for and no more.
 *
 * Writing the whole envelope into every entry would leave one holding a section its
 * own read never returns — correct until that query refetched, and then silently
 * gone. Filtering keeps a patched entry indistinguishable from a freshly fetched one.
 */
export function filterToHeldSections<TSections extends object>(
  held: Partial<TSections>,
  envelope: Envelope<TSections>,
): Envelope<TSections> {
  const changed: Partial<TSections> = {}

  for (const key of Object.keys(envelope.changed) as Array<keyof TSections>) {
    if (key in held) {
      // The index dance is TypeScript's: `changed[key] = envelope.changed[key]` cannot
      // be checked across a union of keys, though every pairing is by construction sound.
      ;(changed as Record<PropertyKey, unknown>)[key] = envelope.changed[key]
    }
  }

  return { ...envelope, changed }
}

/**
 * Patches every listed cache entry, each with only the sections it holds.
 *
 * Entries that have never been fetched are left `undefined` rather than invented —
 * a sheet built out of one mutation's worth of sections would be a lie about what
 * the server would return.
 */
export function patchSheets<TSections extends object, TSheet extends HasSections<TSections>>(
  queryClient: QueryClient,
  keys: readonly QueryKey[],
  envelope: Envelope<TSections>,
): void {
  for (const key of keys) {
    queryClient.setQueryData<TSheet>(key, (previous) =>
      previous === undefined
        ? previous
        : applyEnvelope(previous, filterToHeldSections(previous.sections, envelope)),
    )
  }
}
